import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, CATEGORIES, SUBCATEGORIES, apiError } from "@/lib/api";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const EMPTY = {
  name: "",
  category: "Gemstone Jewellery",
  subcategory: "",
  price: "",
  original_price: "",
  description: "",
  about_html: "",
  main_image: "",
  additional_images: "",
  sku: "",
  stock_quantity: "",
  material: "",
  gemstone: "",
  size: "",
  care_instructions: "",
  featured: false,
  new_arrival: false,
  active: true,
  in_stock: true,
};

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">
      {label}
    </Label>
    {children}
  </div>
);

const inputCls =
  "rounded-none border-cream/15 bg-transparent text-sm text-cream placeholder:text-cream/25 focus-visible:ring-gold focus-visible:border-gold";

const ToggleRow = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between border border-cream/10 px-4 py-3">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60">{label}</span>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </div>
);

const ProductFormDialog = ({ open, onOpenChange, product, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mainFileRef = useRef(null);
  const extraFileRef = useRef(null);

  const uploadFile = async (file, target) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      if (target === "main") {
        setForm((f) => ({ ...f, main_image: data.url }));
      } else {
        setForm((f) => ({
          ...f,
          additional_images: f.additional_images
            ? `${f.additional_images}\n${data.url}`
            : data.url,
        }));
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err, "Upload failed. Please try again."));
    } finally {
      setUploading(false);
    }
  };
  const isEdit = Boolean(product);

  useEffect(() => {
    if (!open) return;
    if (product) {
      setForm({
        name: product.name || "",
        category: product.category || CATEGORIES[0],
        subcategory: product.subcategory || "",
        price: product.price ?? "",
        original_price: product.original_price ?? "",
        description: product.description || "",
        about_html: product.about_html || "",
        main_image: product.images?.[0] || "",
        additional_images: (product.images || []).slice(1).join("\n"),
        sku: product.sku || "",
        stock_quantity: product.stock_quantity ?? "",
        material: product.material || "",
        gemstone: product.gemstone || "",
        size: product.size || "",
        care_instructions: product.care_instructions || "",
        featured: !!product.featured,
        new_arrival: !!product.new_arrival,
        active: product.active !== false,
        in_stock: product.in_stock !== false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, product]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Product name is required");
    if (!form.price || Number(form.price) <= 0) return toast.error("A valid price is required");
    if (!form.main_image.trim()) return toast.error("Main image URL is required");

    const images = [
      form.main_image.trim(),
      ...form.additional_images.split("\n").map((s) => s.trim()).filter(Boolean),
    ];
    const payload = {
      name: form.name.trim(),
      category: form.category,
      subcategory: form.subcategory,
      price: Number(form.price),
      original_price: form.original_price ? Number(form.original_price) : null,
      description: form.description,
      about_html: form.about_html,
      images,
      sku: form.sku,
      stock_quantity: form.stock_quantity === "" ? null : Number(form.stock_quantity),
      material: form.material,
      gemstone: form.gemstone,
      size: form.size,
      care_instructions: form.care_instructions,
      featured: form.featured,
      new_arrival: form.new_arrival,
      active: form.active,
      in_stock: form.in_stock,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/products/${product.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added to the collection");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err, "Could not save the product"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-form-dialog"
        className="max-h-[90vh] overflow-y-auto rounded-none border-gold/30 bg-espresso-surface text-cream sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-cream">
            {isEdit ? "Edit Product" : "Add a New Treasure"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Name *" htmlFor="pf-name">
            <Input id="pf-name" data-testid="product-form-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="Emerald Aura Pendant" />
          </Field>
          <Field label="Category *" htmlFor="pf-category">
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v, subcategory: "" }))}>
              <SelectTrigger id="pf-category" data-testid="product-form-category-select" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none border-gold/30 bg-espresso-surface text-cream">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`product-form-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {(SUBCATEGORIES[form.category] || []).length > 0 && (
            <Field label="Subcategory" htmlFor="pf-subcategory">
              <Select
                value={form.subcategory || "none"}
                onValueChange={(v) => setForm((f) => ({ ...f, subcategory: v === "none" ? "" : v }))}
              >
                <SelectTrigger id="pf-subcategory" data-testid="product-form-subcategory-select" className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-gold/30 bg-espresso-surface text-cream">
                  <SelectItem value="none" data-testid="product-form-subcategory-none">None</SelectItem>
                  {SUBCATEGORIES[form.category].map((s) => (
                    <SelectItem key={s} value={s} data-testid={`product-form-subcategory-${s.toLowerCase()}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Price (₹) *" htmlFor="pf-price">
            <Input id="pf-price" data-testid="product-form-price-input" type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className={inputCls} placeholder="4999" />
          </Field>
          <Field label="Original Price (₹)" htmlFor="pf-original-price">
            <Input id="pf-original-price" data-testid="product-form-original-price-input" type="number" min="0" step="0.01" value={form.original_price} onChange={set("original_price")} className={inputCls} placeholder="6999" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Main Image *" htmlFor="pf-main-image">
              <div className="flex gap-2">
                <Input
                  id="pf-main-image"
                  data-testid="product-form-main-image-input"
                  value={form.main_image}
                  onChange={set("main_image")}
                  className={`${inputCls} flex-1`}
                  placeholder="Upload a photo or paste an image URL…"
                />
                <button
                  type="button"
                  data-testid="product-form-upload-main-button"
                  onClick={() => mainFileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex shrink-0 items-center gap-2 border border-gold/50 px-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-espresso disabled:opacity-50"
                >
                  <Upload size={13} /> {uploading ? "…" : "Upload"}
                </button>
                <input
                  ref={mainFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="product-form-main-file-input"
                  onChange={(e) => {
                    uploadFile(e.target.files?.[0], "main");
                    e.target.value = "";
                  }}
                />
              </div>
            </Field>
            {form.main_image.trim() && (
              <div className="mt-3 h-32 w-32 overflow-hidden border border-gold/30 bg-espresso">
                <img
                  src={form.main_image.trim()}
                  alt="Preview"
                  data-testid="product-form-image-preview"
                  className="h-full w-full object-cover"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <Field label="Additional Images (uploaded or one URL per line)" htmlFor="pf-additional">
              <Textarea id="pf-additional" data-testid="product-form-additional-images-input" rows={2} value={form.additional_images} onChange={set("additional_images")} className={inputCls} placeholder={"https://…\nhttps://…"} />
            </Field>
            <button
              type="button"
              data-testid="product-form-upload-more-button"
              onClick={() => extraFileRef.current?.click()}
              disabled={uploading}
              className="mt-2 inline-flex items-center gap-2 border border-cream/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60 transition-colors hover:border-gold hover:text-gold disabled:opacity-50"
            >
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload another photo"}
            </button>
            <input
              ref={extraFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="product-form-extra-file-input"
              onChange={(e) => {
                uploadFile(e.target.files?.[0], "extra");
                e.target.value = "";
              }}
            />
          </div>
          <div className="sm:col-span-2">
            <Field label="Description" htmlFor="pf-description">
              <Textarea id="pf-description" data-testid="product-form-description-input" rows={3} value={form.description} onChange={set("description")} className={inputCls} placeholder="The story of this piece…" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="About this product (rich text — details, usage instructions)" htmlFor="pf-about">
              <div data-testid="product-form-about-editor" className="border border-cream/15 focus-within:border-gold">
                <ReactQuill
                  theme="snow"
                  value={form.about_html}
                  onChange={(html) => setForm((f) => ({ ...f, about_html: html }))}
                  modules={{
                    toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]],
                  }}
                  placeholder="Detailed information, styling tips, usage instructions…"
                />
              </div>
            </Field>
          </div>
          <Field label="SKU" htmlFor="pf-sku">
            <Input id="pf-sku" data-testid="product-form-sku-input" value={form.sku} onChange={set("sku")} className={inputCls} placeholder="AAR-GP-001" />
          </Field>
          <Field label="Stock Quantity" htmlFor="pf-stock">
            <Input id="pf-stock" data-testid="product-form-stock-input" type="number" min="0" value={form.stock_quantity} onChange={set("stock_quantity")} className={inputCls} placeholder="12" />
          </Field>
          <Field label="Material" htmlFor="pf-material">
            <Input id="pf-material" data-testid="product-form-material-input" value={form.material} onChange={set("material")} className={inputCls} placeholder="22K gold plated brass" />
          </Field>
          <Field label="Gemstone" htmlFor="pf-gemstone">
            <Input id="pf-gemstone" data-testid="product-form-gemstone-input" value={form.gemstone} onChange={set("gemstone")} className={inputCls} placeholder="Emerald" />
          </Field>
          <Field label="Size" htmlFor="pf-size">
            <Input id="pf-size" data-testid="product-form-size-input" value={form.size} onChange={set("size")} className={inputCls} placeholder="Adjustable / 18 in" />
          </Field>
          <Field label="Care Instructions" htmlFor="pf-care">
            <Input id="pf-care" data-testid="product-form-care-input" value={form.care_instructions} onChange={set("care_instructions")} className={inputCls} placeholder="Keep away from perfume & moisture" />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:col-span-2 sm:grid-cols-2">
            <ToggleRow label="Featured (homepage)" testid="product-form-featured-toggle" checked={form.featured} onChange={setBool("featured")} />
            <ToggleRow label="New Arrival" testid="product-form-new-arrival-toggle" checked={form.new_arrival} onChange={setBool("new_arrival")} />
            <ToggleRow label="Active (visible in store)" testid="product-form-active-toggle" checked={form.active} onChange={setBool("active")} />
            <ToggleRow label="In Stock" testid="product-form-in-stock-toggle" checked={form.in_stock} onChange={setBool("in_stock")} />
          </div>
          <div className="flex gap-3 pt-2 sm:col-span-2">
            <button
              type="button"
              data-testid="product-form-cancel-button"
              onClick={() => onOpenChange(false)}
              className="flex-1 border border-cream/20 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/70 transition-colors hover:border-cream/50"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="product-form-save-button"
              disabled={saving}
              className="flex-1 bg-gold py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:opacity-50"
            >
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
